import React, { useCallback, useEffect, useState } from 'react';
import Loading from '../shared/Loading/Loading';
import Button from '../shared/Button/Button';
import StatusBadge from '../shared/StatusBadge/StatusBadge';
import { FormGroup, FormLabel, FormSelect, ErrorMessage } from '../shared/Form/Form.styles';
import { Panel, Notice, IvaTag } from '../../pages/sales/Sales.styles';
import { FilterBar, Pager, EmptyBox } from '../../pages/sales/SalesList.styles';
import { ItemList, ItemButton, LinkButton } from './promoters.styles';
import CutDetailModal, { cutWeekLabel } from './CutDetailModal';
import MarkPaidModal from './MarkPaidModal';
import { cutStatus, kindLabel } from './promoterLabels';
import { formatMoney } from '../../utils/money';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'open', label: 'Por pagar' },
  { value: 'paid', label: 'Pagados' },
];

// Cortes semanales. Una sola vista para los tres roles:
//   viewer 'promoter' : sus cortes (lo que le deben)
//   viewer 'empresa'  : los cortes de sus promotores; puede "Marcar pagado"
//   viewer 'admin'    : todos
// `load(params)` / `loadDetail(id)` son las llamadas del rol. `reloadKey` fuerza
// recargar la lista (p. ej. tras generar cortes). `onError(err)` devuelve true
// si ya manejó el error.
const PromoterCutsView = ({
  viewer,
  load,
  loadDetail,
  onError,
  reloadKey = 0,
  empresaOptions = [],
  promoterOptions = [],
}) => {
  const [filters, setFilters] = useState({ status: 'all', empresaId: '', promoterId: '' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openCut, setOpenCut] = useState(null); // corte abierto en el detalle
  const [paying, setPaying] = useState(null); // corte que se va a marcar pagado
  const [detailVersion, setDetailVersion] = useState(0);
  const [flash, setFlash] = useState('');

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await load({
        status: filters.status,
        empresaId: filters.empresaId,
        promoterId: filters.promoterId,
        page,
        limit: PAGE_SIZE,
      });
      setData(result);
    } catch (err) {
      if (onError && onError(err)) return;
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [load, onError, filters, page]);

  useEffect(() => { fetchPage(); }, [fetchPage, reloadKey]);

  const setFilter = (key) => (event) => {
    setPage(1);
    setFilters({ ...filters, [key]: event.target.value });
  };

  const handlePaid = (cut, message) => {
    setPaying(null);
    setFlash(message || `Corte #${cut.id} marcado como pagado.`);
    // El detalle abierto se vuelve a leer; la lista se actualiza sin perder la página.
    setDetailVersion((v) => v + 1);
    fetchPage();
  };

  const cuts = (data && data.cuts) || [];
  const openSummary = data && data.openSummary;
  const totalPages = (data && data.totalPages) || 1;

  return (
    <>
      {viewer === 'empresa' && openSummary && (
        <Notice $warn={openSummary.cuts > 0} role="status">
          {openSummary.cuts > 0 ? (
            Number(openSummary.totalAmount) < 0 ? (
              <>
                Tienes un saldo a favor de <strong>{formatMoney(Math.abs(openSummary.totalAmount))}</strong> en{' '}
                <strong>{openSummary.cuts}</strong> {openSummary.cuts === 1 ? 'corte abierto' : 'cortes abiertos'} (todos los promotores): los ajustes superan a las comisiones.
              </>
            ) : (
              <>
                Hoy debes <strong>{formatMoney(openSummary.totalAmount)}</strong> en{' '}
                <strong>{openSummary.cuts}</strong> {openSummary.cuts === 1 ? 'corte abierto' : 'cortes abiertos'} (todos los promotores).
              </>
            )
          ) : (
            <>No tienes cortes por pagar.</>
          )}
        </Notice>
      )}

      {flash && <Notice role="status">{flash}</Notice>}

      <Panel>
        <h2>Filtros</h2>
        <FilterBar style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <FormGroup>
            <FormLabel htmlFor="pc-status">Estado</FormLabel>
            <FormSelect id="pc-status" value={filters.status} onChange={setFilter('status')}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </FormSelect>
          </FormGroup>
          {viewer !== 'empresa' && empresaOptions.length > 0 && (
            <FormGroup>
              <FormLabel htmlFor="pc-empresa">Empresa</FormLabel>
              <FormSelect id="pc-empresa" value={filters.empresaId} onChange={setFilter('empresaId')}>
                <option value="">Todas</option>
                {empresaOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </FormSelect>
            </FormGroup>
          )}
          {viewer !== 'promoter' && promoterOptions.length > 0 && (
            <FormGroup>
              <FormLabel htmlFor="pc-promoter">Promotor</FormLabel>
              <FormSelect id="pc-promoter" value={filters.promoterId} onChange={setFilter('promoterId')}>
                <option value="">Todos</option>
                {promoterOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </FormSelect>
            </FormGroup>
          )}
        </FilterBar>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', lineHeight: 1.45 }}>
          Los cortes son semanales (lunes a domingo, hora de México) y se calculan por la <strong>fecha de venta</strong>.
          BokerHub solo registra lo que se debe: el pago lo hace la empresa por su cuenta.
        </p>
      </Panel>

      {error && (
        <ErrorMessage role="alert">
          {error} <LinkButton type="button" onClick={fetchPage}>Reintentar</LinkButton>
        </ErrorMessage>
      )}

      {loading && !data ? (
        <Loading text="Cargando cortes..." />
      ) : cuts.length === 0 && !error ? (
        <EmptyBox>
          <h3>No hay cortes con estos filtros</h3>
          <p>
            {viewer === 'promoter'
              ? 'Tus cortes aparecen aquí cuando termina cada semana con ventas.'
              : 'Los cortes se generan al terminar cada semana con ventas de promotores.'}
          </p>
        </EmptyBox>
      ) : (
        <>
          <ItemList aria-busy={loading}>
            {cuts.map((cut) => {
              const state = cutStatus(cut.status);
              const negative = Number(cut.totalAmount) < 0;
              return (
                <ItemButton
                  key={cut.id}
                  type="button"
                  onClick={() => setOpenCut(cut)}
                  aria-label={`Corte ${cut.id}, semana ${cutWeekLabel(cut)}, ${state.label}. Ver detalle`}
                >
                  <div className="head">
                    <span className="title">Semana {cutWeekLabel(cut)}</span>
                    <StatusBadge status={state.status}>{state.label}</StatusBadge>
                  </div>
                  <div className="meta">
                    Corte #{cut.id}
                    {viewer !== 'promoter' && cut.promoter ? ` · ${cut.promoter.name} (${kindLabel(cut.promoter.kind)})` : ''}
                    {viewer !== 'empresa' && cut.empresa ? ` · ${cut.empresa.businessName}` : ''}
                  </div>
                  <div className="meta">
                    {cut.salesCount} {cut.salesCount === 1 ? 'venta' : 'ventas'} por {formatMoney(cut.salesAmount)} <IvaTag>IVA incluido</IvaTag>
                    {' · '}comisión {formatMoney(cut.commissionAmount)}
                    {cut.adjustmentsCount > 0 ? ` · ${cut.adjustmentsCount} ${cut.adjustmentsCount === 1 ? 'ajuste' : 'ajustes'}: ${formatMoney(cut.adjustmentsAmount)}` : ''}
                  </div>
                  <div className="total">
                    <span>{negative ? 'Saldo a favor de la empresa' : viewer === 'promoter' ? 'Total a recibir' : 'Total a pagar'}</span>
                    <strong data-negative={negative}>{formatMoney(cut.totalAmount)}</strong>
                  </div>
                </ItemButton>
              );
            })}
          </ItemList>

          {totalPages > 1 && (
            <Pager>
              <Button size="small" variant="ghost" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>
                ← Anteriores
              </Button>
              <span>Página {page} de {totalPages} · {data.total} cortes</span>
              <Button size="small" variant="ghost" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>
                Siguientes →
              </Button>
            </Pager>
          )}
        </>
      )}

      {openCut && (
        <CutDetailModal
          cutId={openCut.id}
          initialCut={openCut}
          viewer={viewer}
          load={loadDetail}
          onError={onError}
          onClose={() => setOpenCut(null)}
          onMarkPaid={viewer === 'empresa' ? setPaying : undefined}
          refreshKey={detailVersion}
          locked={Boolean(paying)}
        />
      )}
      {paying && (
        <MarkPaidModal
          cut={paying}
          onClose={() => setPaying(null)}
          onPaid={handlePaid}
          onError={onError}
        />
      )}
    </>
  );
};

export default PromoterCutsView;
